import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { ResolverTag } from '@ps/web-core-enums';
import type { TrackedTranslation, TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import { StarsLink, translatedLink } from '@ps/web-shared-ui';
import React from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import BonusCardImg from '@swf/components/StarsMPNotification/assets/icons/instant-bonus-badge.png';
import SplitWagerExplainer from '@swf/components/StarsMPNotification/components/SplitWagerExplainer/SplitWagerExplainer';
import type { CardViewProps } from '@swf/components/StarsMPNotification/config/NotificationTypes';
import { InstantBonusClearanceType } from '@swf/components/StarsMPNotification/enums';
import { Variant as CloseIconVariant } from '@swf/components/StarsModal/components/CloseButton';
import LicensedTranslation from '@swf/components/Translation/LicensedTranslation';
import useOpenWindow from '@swf/core/hooks/useOpenWindow';
import type {
  GameTypesAndVariants,
  SplitWagerBonusMPNotification
} from '@swf/core/types/Notification';

import SharedStyles from '../../styles/shared.scss';
import Attributes from '../Attributes/Attributes';
import Buttons, { GroupVariant as ButtonGroupVariant } from '../Buttons/Buttons';
import CardBadge from '../CardBadge/CardBadge';
// TODO refactor to use composition or shared.scss
// eslint-disable-next-line no-restricted-imports
import DescriptionStyles from '../CardDescription/CardDescription.scss';
import Header, { HeaderVariant } from '../Header/Header';
import Title from '../Title/Title';

import {
  TXTWCL_All_Casino_games_eligible,
  TXTWCL_Earn_N0_XaXredemption_pointsX__ELL,
  TXTWCL_Place_N0_bets_in_eligible_game_ELL,
  TXTWCL_View_eligible_games,
  TXTWCL_Wager_N0_in_eligible_games_to__ELL,
  TXTWCL_You_can_withdraw_your_share_at_ELL,
  TXTWCL_You_can_withdraw_your_share_at_ELL_1
} from './InstantBonus.messages.json';

interface CardDataProps {
  cardData: SplitWagerBonusMPNotification;
}

const descriptionTagOptions = {
  funds: TXTWCL_Wager_N0_in_eligible_games_to__ELL,
  points: TXTWCL_Earn_N0_XaXredemption_pointsX__ELL,
  bets: TXTWCL_Place_N0_bets_in_eligible_game_ELL
};

const descriptionValueKeys = {
  [InstantBonusClearanceType.Funds]: 'stakesToClear',
  [InstantBonusClearanceType.Points]: 'pointsToClear',
  [InstantBonusClearanceType.Bets]: 'betsToClear'
};

const TermsAndConditions: React.FC<{
  translationTag: TrackedTranslation;
  licenses: LicenceId[];
  isCappedWinnings: boolean;
}> = ({ translationTag, licenses, isCappedWinnings }) => {
  const resolveLink = useOpenWindow({
    resolverTag: isCappedWinnings
      ? ResolverTag.InstantBonusSplitWagerTermsCashCap
      : ResolverTag.InstantBonusSplitWagerTermsCash
  });
  return (
    <StarsLink onClick={resolveLink}>
      <LicensedTranslation
        include={licenses}
        {...translationTag}
        values={{
          a: (msg: React.ReactNode) => msg
        }}
      />
    </StarsLink>
  );
};

const EligibleGamesLink: React.FC<{
  shortNames?: string;
  longNames?: string;
  supportedGamesJson: GameTypesAndVariants[];
  action: () => void;
}> = ({ shortNames, longNames, supportedGamesJson, action }) => {
  const Link: React.FC<{ linkText?: string; linkTranslationProps: TranslationTag }> = ({
    linkText,
    linkTranslationProps
  }) => (
    <button className={SharedStyles.moreInfoButton} onClick={action}>
      {linkText || <Translation {...linkTranslationProps} />}
    </button>
  );

  if (supportedGamesJson && supportedGamesJson[0] && supportedGamesJson[0].C?.length === 0) {
    return <Translation tagName="div" {...TXTWCL_All_Casino_games_eligible} />;
  }
  return (
    <div>
      {longNames && (
        <Link linkText={shortNames} linkTranslationProps={TXTWCL_View_eligible_games} />
      )}
      {!longNames && shortNames && shortNames}
      {!longNames && !shortNames && <Link linkTranslationProps={TXTWCL_View_eligible_games} />}
    </div>
  );
};

/**
 * Front card of a 'InstantBonus'
 * Currently supports
 * 1. SplitWager acceptance notification.
 */
const InstantBonusView: React.FC<CardViewProps & CardDataProps> = ({
  cardData: {
    type,
    attributes,
    attributes: {
      costCash,
      costRatio,
      longEligibleGameNames,
      shortEligibleGameNames,
      supportedGamesJson,
      topUpAdded,
      topUpRatio,
      currencyCode,
      clearanceType,
      maxWinnings
    }
  },
  viewConfig: {
    title: titleKey,
    moreInfoLabel,
    displayAttributes,
    actions,
    defaultHeaderImageURL,
    showCloseIcon
  },
  onToggleFlip,
  onClickPrimaryAction,
  onClickSecondaryAction,
  onClickClose,
  locale,
  license
}) => (
  <>
    <Header
      showCloseIcon={showCloseIcon}
      closeIconVariant={CloseIconVariant.Light}
      headerImageURL={defaultHeaderImageURL}
      defaultHeaderImageURL={defaultHeaderImageURL}
      onClickClose={onClickClose}
      variant={HeaderVariant.Large}
    />
    <section className={SharedStyles.notificationBody}>
      <CardBadge iconImg={BonusCardImg} />
      <Title titleKey={titleKey} attributes={attributes} />
      <SplitWagerExplainer
        cost={costCash}
        topUp={topUpAdded}
        costRatio={costRatio}
        topUpRatio={topUpRatio}
        currency={currencyCode}
        locale={locale}
      />
      <div className={DescriptionStyles.description}>
        <ul className={SharedStyles.dashList}>
          <li>
            <Translation
              tagName="div"
              {...descriptionTagOptions[clearanceType]}
              values={{
                0:
                  clearanceType === InstantBonusClearanceType.Funds ? (
                    <CurrencyAmount
                      value={
                        attributes[
                          descriptionValueKeys[
                            clearanceType as keyof typeof descriptionValueKeys
                          ] as keyof typeof attributes
                        ] as number
                      }
                      currency={currencyCode}
                      minimumFractionDigits={
                        Number.isInteger(
                          (attributes[
                            descriptionValueKeys[
                              clearanceType as keyof typeof descriptionValueKeys
                            ] as keyof typeof attributes
                          ] as number) / 100
                        )
                          ? 0
                          : 2
                      }
                    />
                  ) : (
                    (attributes[
                      descriptionValueKeys[
                        clearanceType as keyof typeof descriptionValueKeys
                      ] as keyof typeof attributes
                    ] as string | number)
                  ),
                a: translatedLink({
                  onClick: useOpenWindow({ resolverTag: ResolverTag.CasinoRedemption })
                })
              }}
            />
          </li>
          <li>
            <Translation
              {...(license === LicenceId.ITALY
                ? TXTWCL_You_can_withdraw_your_share_at_ELL_1
                : TXTWCL_You_can_withdraw_your_share_at_ELL)}
            />
          </li>
          <li>
            <EligibleGamesLink
              shortNames={shortEligibleGameNames}
              longNames={longEligibleGameNames}
              supportedGamesJson={supportedGamesJson as GameTypesAndVariants[]}
              action={onToggleFlip}
            />
          </li>
        </ul>
      </div>
      <Attributes displayAttributes={displayAttributes} attributes={attributes} locale={locale} />
      {moreInfoLabel && (
        <TermsAndConditions
          translationTag={moreInfoLabel.tag}
          licenses={[LicenceId.UK]}
          isCappedWinnings={maxWinnings ? maxWinnings > 0 : false}
        />
      )}
      <Buttons
        actionList={actions}
        notificationType={type}
        onClickPrimaryAction={onClickPrimaryAction}
        onClickSecondaryAction={onClickSecondaryAction}
        attributes={attributes}
        groupVariant={ButtonGroupVariant.Stacked}
      />
    </section>
  </>
);

export default InstantBonusView;
